import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { clearCache } from "@/pages/admin/adminCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";
const HTTP_UNAUTHORIZED = 401;
const IS_DEV = process.env.NODE_ENV !== "production";

function devWarn(...args) {
  if (IS_DEV) console.warn(...args);
}

/**
 * useAdminContacts — handles admin auth-key persistence, fetching, and
 * mutation of contact submissions.
 *
 * SECURITY NOTE: The admin key is stored in sessionStorage (cleared on
 * tab close) rather than httpOnly cookies. This is an intentional
 * tradeoff — the admin endpoint uses simple header-based auth (no
 * server-side session store), and a single key gates a single-user
 * tool. Moving to httpOnly cookies would require adding a session
 * server, login endpoint, and CSRF protection — disproportionate
 * scope for this surface. Rotate the key before public launch.
 */
export default function useAdminContacts() {
  const [key, setKey] = useState(
    () => sessionStorage.getItem(KEY_STORAGE) || ""
  );
  const [contacts, setContacts] = useState([]);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const fetchContacts = useCallback(async (adminKey) => {
    if (!adminKey) return;
    setStatus({ state: "loading", error: "" });
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setContacts(res.data || []);
      setStatus({ state: "ready", error: "" });
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        sessionStorage.removeItem(KEY_STORAGE);
        setKey("");
        setStatus({
          state: "unauthorized",
          error: "That key isn't valid. Try again.",
        });
      } else {
        setStatus({ state: "error", error: "Could not load submissions." });
      }
    }
  }, []);

  useEffect(() => {
    if (key) fetchContacts(key);
  }, [key, fetchContacts]);

  const unlock = useCallback((adminKey) => {
    sessionStorage.setItem(KEY_STORAGE, adminKey);
    setKey(adminKey);
  }, []);

  const lock = useCallback(() => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    setKey("");
    setContacts([]);
    setStatus({ state: "idle", error: "" });
  }, []);

  const markRead = useCallback(
    async (id) => {
      try {
        await axios.patch(
          `${API}/contact/${id}`,
          { is_read: true },
          { headers: { "X-Admin-Key": key } }
        );
        setContacts((cs) =>
          cs.map((c) => (c.id === id ? { ...c, is_read: true } : c))
        );
      } catch (err) {
        devWarn("[VBB] markRead failed", err);
      }
    },
    [key]
  );

  const deleteContact = useCallback(
    async (id) => {
      try {
        await axios.delete(`${API}/contact/${id}`, {
          headers: { "X-Admin-Key": key },
        });
        setContacts((cs) => cs.filter((c) => c.id !== id));
        return true;
      } catch (err) {
        devWarn("[VBB] delete failed", err);
        return false;
      }
    },
    [key]
  );

  // ---- Batch selection ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const batchMarkRead = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return { ok: true, count: 0 };
    try {
      const res = await axios.post(
        `${API}/contact/batch/read`,
        { ids },
        { headers: { "X-Admin-Key": key } }
      );
      setContacts((cs) =>
        cs.map((c) => (selectedIds.has(c.id) ? { ...c, is_read: true } : c))
      );
      clearSelection();
      return { ok: true, count: res.data?.modified ?? 0 };
    } catch (err) {
      devWarn("[VBB] batchMarkRead failed", err);
      return { ok: false, count: 0 };
    }
  }, [selectedIds, key, clearSelection]);

  const batchDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return { ok: true, count: 0 };
    try {
      const res = await axios.post(
        `${API}/contact/batch/delete`,
        { ids },
        { headers: { "X-Admin-Key": key } }
      );
      setContacts((cs) => cs.filter((c) => !selectedIds.has(c.id)));
      clearSelection();
      return { ok: true, count: res.data?.deleted ?? 0 };
    } catch (err) {
      devWarn("[VBB] batchDelete failed", err);
      return { ok: false, count: 0 };
    }
  }, [selectedIds, key, clearSelection]);

  return {
    key,
    contacts,
    status,
    fetchContacts,
    unlock,
    lock,
    markRead,
    deleteContact,
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    batchMarkRead,
    batchDelete,
  };
}
